import { Box, LinearProgress, Skeleton, Typography } from '@mui/material';
import { Cell, Legend, Pie, PieChart, Tooltip } from 'recharts';
import { useTranslation } from 'react-i18next';
import { useDashboard } from '../DashboardContext';
import DashboardWidget, { DashboardSizedChart } from './DashboardWidget';

import { addMoney } from "../../../util/quantity";
const COLORS = ['#00695c', '#0288d1', '#ed6c02', '#7b1fa2', '#455a64', '#c2185b'];

const SupplierPaymentSummaryBody = () => {
  const { supplierPaymentModes, currency } = useDashboard();
  const { t } = useTranslation();

  const formatValue = (value: number) =>
    new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value) + ` ${currency}`;

  const total = supplierPaymentModes.reduce((sum, row) => addMoney(sum, row.amount), 0);
  const positiveModes = supplierPaymentModes.filter((row) => row.amount > 0);

  if (positiveModes.length === 0 || total === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
        <Typography>{t('common:dashboard.noData')}</Typography>
      </Box>
    );
  }

  return (
    <>
      <DashboardSizedChart height={220}>
        <PieChart>
          <Pie
            data={positiveModes}
            cx="50%"
            cy="50%"
            innerRadius={55}
            outerRadius={85}
            paddingAngle={3}
            dataKey="amount"
            nameKey="mode"
          >
            {positiveModes.map((_, idx) => (
              <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value: number) => formatValue(value)} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </DashboardSizedChart>

      <Box sx={{ mt: 2, flexShrink: 0 }}>
        {positiveModes.map((row, idx) => {
          const pct = total > 0 ? (row.amount / total) * 100 : 0;
          return (
            <Box key={row.mode} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>{row.mode}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatValue(row.amount)} ({pct.toFixed(1)}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: COLORS[idx % COLORS.length],
                    borderRadius: 3,
                  },
                }}
              />
            </Box>
          );
        })}
      </Box>
    </>
  );
};

const SupplierPaymentSummary = () => {
  const { sectionLoading } = useDashboard();
  const { t } = useTranslation();

  if (sectionLoading.activity) {
    return (
      <DashboardWidget
        title={t('common:dashboard.supplierPayments.title')}
        helpKey="supplierPayments.title"
        chartHeight={380}
        fitContent
      >
        <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto' }} />
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget
      title={t('common:dashboard.supplierPayments.title')}
      helpKey="supplierPayments.title"
      chartHeight={220}
      fitContent
    >
      <SupplierPaymentSummaryBody />
    </DashboardWidget>
  );
};

export default SupplierPaymentSummary;